


















































import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  ref,
  watch,
} from '@nuxtjs/composition-api'
import useClickOutside from '@/components/Sections/section-find-nearest-office/useClickOutside'
import { useTrackScrollDistance } from '@/composition/useTrackScrollDistance'
import TeleportV2 from '@/components/TeleportV2.vue'

const getCalcStyle = (measure: number, offset: string | number, sign = '+') => {
  if (!offset) return measure + 'px'
  if (typeof offset === 'number' && !isNaN(offset)) {
    return `calc(${measure}px ${sign} ${offset}px)`
  }
  return `calc(${measure}px ${sign} (${offset}))`
}
export default defineComponent({
  name: 'PopupTooltip',
  components: { TeleportV2 },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    to: {
      type: String,
      default: '.main-layout',
    },
    width: {
      type: String,
      default: '43rem',
    },
    trigger: {
      type: String,
      default: '',
    },
    popupId: {
      type: String,
      default: '',
    },
    withTail: {
      type: Boolean,
      default: false,
    },
    layout: {
      type: String,
      default: '',
    },
    fixedPlacement: {
      type: String,
      default: '',
    },
    useTrackScrollDistance: {
      type: Boolean,
      default: false,
    },
    fixedOffsetX: {
      type: [String, Number],
      default: '',
    },
    fixedOffsetY: {
      type: [String, Number],
      default: '',
    },
  },
  setup(props, { emit }) {
    const close = () => {
      emit('close')
    }
    const noop = () => {}
    const selectors = computed(() => {
      return [props.trigger, `#${props.popupId}`]
    })

    const actualPlacement = ref(props.fixedPlacement)

    useClickOutside(selectors.value, close)

    useTrackScrollDistance(props.useTrackScrollDistance ? close : noop)

    let triggerElement: HTMLElement = null
    let popupElement: HTMLElement = null

    const style = ref({ popup: null, tail: null })
    let updateId = null

    const calculateFixedPlacement = (placement: string) => {
      if (!triggerElement || !popupElement) return null
      const triggerRect = triggerElement.getBoundingClientRect()
      const popupRect = popupElement.getBoundingClientRect()
      let placementParams = null
      switch (placement) {
        case 'bottom-end':
          placementParams = {
            left: Math.floor(
              triggerRect.left + triggerRect.width / 2 - popupRect.width
            ),
            top: Math.floor(triggerRect.top + triggerRect.height),
          }
          break
        case 'bottom-center':
          placementParams = {
            left: Math.floor(
              triggerRect.left + triggerRect.width / 2 - popupRect.width / 2
            ),
            top: Math.floor(triggerRect.top + triggerRect.height),
          }
          break
        case 'top-end':
          placementParams = {
            left: Math.floor(
              triggerRect.left + (triggerRect.width / 2 - popupRect.width)
            ),
            top: Math.floor(triggerRect.top - popupRect.height),
          }
          break
      }

      if (placementParams) {
        const tX = getCalcStyle(placementParams.left, props.fixedOffsetX)
        const tY = getCalcStyle(
          placementParams.top,
          props.fixedOffsetY,
          placement === 'top-end' ? '-' : '+'
        )
        return {
          popup: { transform: `translate(${tX}, ${tY})` },
          tail: {
            right: '5px',
            top: placement === 'top-end' ? '-1px' : '1px',
            transform: placement === 'top-end' ? 'scaleY(-1)' : '',
          },
        }
      }

      return { popup: null, tail: null }
    }

    const animate = () => {
      style.value = calculateFixedPlacement(actualPlacement.value)
      updateId = requestAnimationFrame(animate)
    }

    const recalculateFixedPlacement = (popupElement) => {
      const rect = popupElement.getBoundingClientRect()
      actualPlacement.value = props.fixedPlacement
      if (
        props.fixedPlacement === 'bottom-end' &&
        rect.bottom > window.innerHeight
      )
        actualPlacement.value = 'top-end'
      animate()
    }

    watch(
      () => props.isVisible,
      () => {
        if (!props.isVisible || !props.fixedPlacement) {
          triggerElement = null
          popupElement = null
          cancelAnimationFrame(updateId)
          return
        }
        nextTick(() => {
          triggerElement = document.querySelector(props.trigger)
          popupElement = document.querySelector(`#${props.popupId}`)
          style.value = calculateFixedPlacement(props.fixedPlacement)
          nextTick(
            () => popupElement && recalculateFixedPlacement(popupElement)
          )
        })
      },
      { immediate: true }
    )

    onBeforeUnmount(() => {
      cancelAnimationFrame(updateId)
    })

    return {
      style,
      actualPlacement,
    }
  },
})
